import React, { PureComponent } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import autobind from 'autobind-decorator';
import classNames from 'classnames';
import IntercomLauncher from 'src/components/IntercomLauncher';
import uiDux from 'src/redux/ui';
import undoDux from 'src/redux/undo';
import constants from 'src/constants';
import breakpointUtils from 'src/utils/breakpointUtils';
import 'pepjs';

import {
  ControlBarContainer,
  CameraButton,
  MenuItem,
  ShareButton,
  UndoRedoButton,
  ViewNavigatorMenuButton,
} from './styled';

const { MODE_NAMES } = constants.app;
const { VIEW_OPTIONS } = constants.machete;

export const mapStateToProps = (state) => ({
  isRedoEnabled: undoDux.selectors.isRedoEnabled(state),
  isUndoEnabled: undoDux.selectors.isUndoEnabled(state),
  shouldRenderUndoRedoButtons: uiDux.selectors.shouldRenderUndoRedoButtons(
    state,
  ),
  isSharedJob: !!state.designPro.app.shareToken,
  isMeasureModeActive: state.designPro.app.activeMode === MODE_NAMES.MEASURE,
  isScreenshotModeEnabled: uiDux.selectors.isScreenshotModeEnabled(state),
  user: state.designPro.user.userProfile,
});

export const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      handleUndoClick: undoDux.actions.handleUndoClick,
      handleRedoClick: undoDux.actions.handleRedoClick,
      handleShareClick: uiDux.actions.handleShareClick,
      handleViewOptionClick: (id) => uiDux.actions.handleViewOptionClick(id),
      handleScreenshotModeClick: uiDux.actions.handleScreenshotModeClick,
    },
    dispatch,
  );

export class ControlBar extends PureComponent {
  // eslint-disable-next-line
  static propTypes = {
    handleRedoClick: PropTypes.func.isRequired,
    handleScreenshotModeClick: PropTypes.func.isRequired,
    handleShareClick: PropTypes.func.isRequired,
    handleUndoClick: PropTypes.func.isRequired,
    handleViewOptionClick: PropTypes.func.isRequired,
    isMeasureModeActive: PropTypes.bool.isRequired,
    isRedoEnabled: PropTypes.bool.isRequired,
    isScreenshotModeEnabled: PropTypes.bool.isRequired,
    isUndoEnabled: PropTypes.bool.isRequired,
    isSharedJob: PropTypes.bool.isRequired,
    shouldRenderUndoRedoButtons: PropTypes.bool.isRequired,
  };

  constructor(props) {
    super(props);
    this.containerRef = React.createRef();
  }

  state = {
    isViewMenuOpen: false,
  };

  componentDidMount() {
    document.addEventListener('click', this.handleDocumentClick);
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleDocumentClick);
  }

  @autobind
  hideMenus() {
    this.setState({
      isViewMenuOpen: false,
    });
  }

  @autobind
  handleDocumentClick(e) {
    if (!this.containerRef.current.contains(e.target)) {
      this.hideMenus();
    }
  }

  @autobind
  toggleViewMenu() {
    this.setState({
      isViewMenuOpen: !this.state.isViewMenuOpen,
    });
  }

  @autobind
  onUndoClick() {
    this.props.handleUndoClick();
    this.hideMenus();
  }

  @autobind
  onRedoClick() {
    this.props.handleRedoClick();
    this.hideMenus();
  }

  @autobind
  openJobShare() {
    this.props.handleShareClick();
    this.hideMenus();
  }

  @autobind
  onViewOptionClick(viewId) {
    this.props.handleViewOptionClick(viewId);
    this.toggleViewMenu();
  }

  @autobind
  renderViewOptions() {
    const optionsClassNames = classNames('view-options', {
      hide: !this.state.isViewMenuOpen,
    });

    return (
      <div className={optionsClassNames}>
        {Object.values(VIEW_OPTIONS).map((view) => {
          return (
            <MenuItem
              key={`view-option-${view.id}`}
              data-selenium={`${view.id}Button`}
              onClick={() => this.onViewOptionClick(view.id)}
            >
              {view.displayName}
            </MenuItem>
          );
        })}
      </div>
    );
  }

  render() {
    const {
      handleScreenshotModeClick,
      isMeasureModeActive,
      isRedoEnabled,
      isScreenshotModeEnabled,
      isUndoEnabled,
      isSharedJob,
      shouldRenderUndoRedoButtons,
    } = this.props;

    const isOnMobileDevice = breakpointUtils.isMobileDevice();
    const { isViewMenuOpen } = this.state;

    return (
      <div ref={this.containerRef}>
        <ControlBarContainer
          isOnlyShareRendered={isOnMobileDevice}
          data-selenium="controlBar"
        >
          {isScreenshotModeEnabled &&
            !isSharedJob &&
            !isOnMobileDevice &&
            !isMeasureModeActive && (
              <CameraButton
                onClick={handleScreenshotModeClick}
                data-selenium="cameraButton"
              >
                Camera
              </CameraButton>
            )}
          {!isSharedJob && (
            <ShareButton
              onClick={this.openJobShare}
              data-selenium="shareButton"
            >
              Share
            </ShareButton>
          )}
          {!isOnMobileDevice && (
            <>
              <ViewNavigatorMenuButton
                onClick={this.toggleViewMenu}
                isExpanded={isViewMenuOpen}
                data-selenium="viewButton"
              >
                View
              </ViewNavigatorMenuButton>
              {shouldRenderUndoRedoButtons && (
                <>
                  <UndoRedoButton
                    onClick={this.onUndoClick}
                    disabled={!isUndoEnabled}
                    data-ext-sidebar-click-whitelist
                    data-selenium="undoButton"
                  >
                    Undo
                  </UndoRedoButton>
                  <UndoRedoButton
                    onClick={this.onRedoClick}
                    disabled={!isRedoEnabled}
                    data-ext-sidebar-click-whitelist
                    data-selenium="redoButton"
                  >
                    Redo
                  </UndoRedoButton>
                </>
              )}
              {this.renderViewOptions()}
              <IntercomLauncher user={this.props.user} />
            </>
          )}
        </ControlBarContainer>
      </div>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ControlBar);
